import React from "react"
import { Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from "@carrotfertility/carotene-core"
import { ContentfulMarkdown } from "#/services/contentful/contentful-markdown-renderer"
import type { DiagramNode, DiagramRow } from "../renderer/types"
import { ContentItem } from "./content-item"

// Ragged rows render as authored.
function TableCells({ row }: { row: DiagramRow }) {
  return (
    <TableRow>
      {row.cells.map((cell, i) => (
        <TableCell key={cell?.id ?? `empty-${i}`}>{cell && <ContentItem content={cell} />}</TableCell>
      ))}
    </TableRow>
  )
}

function DiagramTable({ content }: { content: DiagramNode }) {
  const [headerRow, ...bodyRows] = content.rows
  return (
    <TableContainer>
      <Table>
        {content.caption && (
          <caption>
            <ContentfulMarkdown>{content.caption}</ContentfulMarkdown>
          </caption>
        )}
        <TableHead>
          <TableCells row={headerRow} />
        </TableHead>
        <TableBody>
          {bodyRows.map((row) => (
            <TableCells key={row.id} row={row} />
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  )
}

function DiagramInfographic({ content }: { content: DiagramNode }) {
  return (
    <Box component="figure" sx={{ marginBlock: 0, marginInline: 0 }}>
      {content.rows.map((row) => (
        <Box
          key={row.id}
          sx={(theme) => ({
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            gap: theme.spacing(theme.tokens.spacing.md),
            marginBlockEnd: theme.spacing(theme.tokens.spacing.md)
          })}
        >
          {row.cells.filter(Boolean).map((cell) => (
            <ContentItem key={cell.id} content={cell} />
          ))}
        </Box>
      ))}
      {content.caption && (
        <figcaption>
          <ContentfulMarkdown>{content.caption}</ContentfulMarkdown>
        </figcaption>
      )}
    </Box>
  )
}

export function DiagramContent({ content }: { content: DiagramNode }) {
  if (content.rows.length === 0) return null
  return content.variant === "infographic" ? (
    <DiagramInfographic content={content} />
  ) : (
    <DiagramTable content={content} />
  )
}
